import { FC, Fragment, useMemo, useState } from "react"
import { CellContext } from "@tanstack/react-table"
import { ChevronRight, X } from "lucide-react"

import Table from "../table"
import Select from "../form/select"
import TimestampCell from "./cells/TimestampCell"
import StandardCell from "./cells/StandardCell"
import PricingProfileCell from "./cells/PricingProfileCell"
import ButtonGroup from "../button-group"
import PricingLogCardDetails from "./pricing-log-card-details"
import MonthlyStats, { ChartClickData } from "./monthly-stats"
import {
  useFetchBrandsQuery,
  useFetchRateflowLogsQuery
} from "../../../redux/queries"
import { useAppSelector } from "../../hooks"
import { selectUserDetails, selectUserRoles } from "../../../redux/slices/auth"
import {
  checkIsSuperAdmin,
  formatPricingLogTypeColumn,
  isNotZeroString
} from "../../utils"
import {
  BBColumnDef,
  PricingLog,
  PricingLogQuery,
  SelectOption,
  SelectOptions
} from "../../../types"

interface Props {
  id?: PricingLogQuery
  isBrandVisible?: boolean
  showBrandSelector?: boolean
}

const getClassName = (props: CellContext<PricingLog, unknown>) => {
  const { base_rate } = props.row.original
  return isNotZeroString(base_rate) ? "" : "bg-red-50"
}

enum PRICING_LOGS_OPTION_IDS {
  ALL_REQUESTS = "all-requests",
  SUCCESSFUL_REQUESTS = "successful-requests",
  FAILED_REQUESTS = "failed-requests"
}

const PRICING_LOGS_OPTIONS: SelectOptions = [
  {
    id: PRICING_LOGS_OPTION_IDS.ALL_REQUESTS,
    name: "All Requests"
  },
  {
    id: PRICING_LOGS_OPTION_IDS.SUCCESSFUL_REQUESTS,
    name: "Successful Requests"
  },
  {
    id: PRICING_LOGS_OPTION_IDS.FAILED_REQUESTS,
    name: "Failed Requests"
  }
]

const ALL_BRANDS_OPTION: SelectOption = { id: "all-brands", name: "All Brands" }

const PricingLogs: FC<Props> = ({ id, isBrandVisible, showBrandSelector }) => {
  const userRoles = useAppSelector(selectUserRoles)
  const isSuperAdmin = checkIsSuperAdmin(userRoles)
  const user = useAppSelector(selectUserDetails)
  const [period, setPeriod] = useState<number>(1)
  const [selected, setSelected] = useState<PricingLog>()
  const [selectedOption, setSelectedOption] = useState<string>(
    PRICING_LOGS_OPTIONS[0].id
  )
  const [selectedBrand, setSelectedBrand] = useState<string>(
    () => id?.bank_id ?? ALL_BRANDS_OPTION.id
  )
  const [chartFilter, setChartFilter] = useState<ChartClickData | null>(null)

  const { data: brands = [] } = useFetchBrandsQuery(
    isSuperAdmin ? { limit: 999 } : { org_id: user?.org?.id, limit: 999 },
    {
      skip: !showBrandSelector
    }
  )

  // Build options for the brand select
  const brandOptions: SelectOptions = useMemo(
    () => [
      ALL_BRANDS_OPTION,
      ...brands.map(brand => ({ id: brand.id, name: brand.name }))
    ],
    [brands]
  )

  // Determine the current id for the query
  const currentId: PricingLogQuery | undefined = useMemo(() => {
    return showBrandSelector && selectedBrand !== ALL_BRANDS_OPTION.id
      ? { bank_id: selectedBrand }
      : id
  }, [id, selectedBrand, showBrandSelector])

  // Calculate date range based on chartFilter or default period
  const { minCreated, maxCreated } = useMemo(() => {
    if (chartFilter) {
      // Use chart filter to determine exact date range
      if (chartFilter.viewType === "daily") {
        // Parse ISO date string (e.g., "2025-10-29")
        const date = new Date(chartFilter.period)
        const startOfDay = new Date(date.setHours(0, 0, 0, 0))
        const endOfDay = new Date(date.setHours(23, 59, 59, 999))

        return {
          minCreated: Math.floor(startOfDay.getTime() / 1000),
          maxCreated: Math.floor(endOfDay.getTime() / 1000)
        }
      } else {
        // Monthly view: "2025-January"
        const [year, monthName] = chartFilter.period.split("-")
        const monthMap: Record<string, number> = {
          January: 0,
          February: 1,
          March: 2,
          April: 3,
          May: 4,
          June: 5,
          July: 6,
          August: 7,
          September: 8,
          October: 9,
          November: 10,
          December: 11
        }
        const month = monthMap[monthName]

        const startOfMonth = new Date(parseInt(year), month, 1, 0, 0, 0, 0)
        const endOfMonth = new Date(
          parseInt(year),
          month + 1,
          0,
          23,
          59,
          59,
          999
        )

        return {
          minCreated: Math.floor(startOfMonth.getTime() / 1000),
          maxCreated: Math.floor(endOfMonth.getTime() / 1000)
        }
      }
    } else {
      // Default behavior: use period buttons
      const now = new Date().getTime()
      return {
        minCreated: Math.floor((now - period * 24 * 3600 * 1000) / 1000),
        maxCreated: undefined
      }
    }
  }, [period, chartFilter])

  const { data: pricingLogsAll, isFetching: isPricingLogsLoading } =
    useFetchRateflowLogsQuery({
      min_created: minCreated,
      max_created: maxCreated,
      success_only: chartFilter?.status === "success" ? 1 : undefined,
      failed_only: chartFilter?.status === "failed" ? 1 : undefined,
      ...currentId
    })

  const pricingLogs = useMemo(
    () =>
      currentId?.bank_id
        ? pricingLogsAll?.filter(pl => pl.brand_id === currentId.bank_id)
        : pricingLogsAll,
    [currentId?.bank_id, pricingLogsAll]
  )

  const sortedList = useMemo(
    () =>
      pricingLogs?.length
        ? [...pricingLogs].sort(
            (a, b) => parseInt(b.timestamp) - parseInt(a.timestamp)
          )
        : [],
    [pricingLogs]
  )

  const columns: BBColumnDef<PricingLog>[] = useMemo(
    () => [
      {
        id: "timestamp",
        header: "Timestamp",
        accessorFn: row => row.timestamp,
        footer: props => props.column.id,
        cell: props => (
          <TimestampCell
            value={props.getValue() as string}
            className={`pl-2 pr-3 py-4 hidden md:table-cell ${getClassName(
              props
            )}`}
          />
        ),
        meta: { headerClassName: "hidden md:table-cell" }
      },
      {
        id: "ob_profile_name",
        header: "Pricing Profile",
        accessorFn: row => row.ob_profile_name,
        footer: props => props.column.id,
        cell: props => (
          <PricingProfileCell
            value={props.row.original}
            className={getClassName(props)}
          />
        )
      },
      {
        id: "pricing-log-id",
        header: "Quote ID",
        meta: { headerClassName: "hidden" },
        accessorFn: row => row.id,
        cell: () => <></>
      }
    ],
    []
  )

  const loanOfficerColumn: BBColumnDef<PricingLog> = useMemo(
    () => ({
      id: "lo_name",
      header: "Loan Officer",
      accessorFn: row => row.lo_name,
      footer: props => props.column.id,
      cell: props => (
        <StandardCell
          value={props.getValue() as string}
          className={`py-4 hidden md:table-cell ${getClassName(props)}`}
        />
      ),
      meta: {
        headerClassName: "hidden md:table-cell"
      }
    }),
    []
  )

  const brandColumn: BBColumnDef<PricingLog> = useMemo(
    () => ({
      id: "brand_name",
      header: "Brand",
      accessorFn: row => row.brand_name,
      footer: props => props.column.id,
      cell: props => (
        <StandardCell
          value={props.getValue() as string}
          className={`py-4 hidden md:table-cell ${getClassName(props)}`}
        />
      ),
      meta: { headerClassName: "hidden md:table-cell" }
    }),
    []
  )

  const typeColumn: BBColumnDef<PricingLog> = useMemo(
    () => ({
      id: "type",
      header: "Type",
      accessorFn: row => row.source,
      footer: props => props.column.id,
      cell: props => (
        <StandardCell
          value={formatPricingLogTypeColumn(props.getValue() as string)}
          className={`py-4 hidden md:table-cell ${getClassName(props)}`}
        />
      ),
      meta: { headerClassName: "hidden md:table-cell" }
    }),
    []
  )

  const editColumn: BBColumnDef<PricingLog> = useMemo(
    () => ({
      id: "edit",
      header: "",
      footer: props => props.column.id,
      cell: props => (
        <td className={`text-center ${getClassName(props)}`}>
          <ChevronRight
            onClick={() => setSelected(props.row.original)}
            className="cursor-pointer ml-auto"
          />
        </td>
      )
    }),
    []
  )

  const tableColumns = useMemo(() => {
    if (!currentId?.loid) {
      return [...columns, loanOfficerColumn, typeColumn, editColumn]
    }
    if (isBrandVisible) {
      return [...columns, brandColumn, typeColumn, editColumn]
    }

    return [...columns, typeColumn, editColumn]
  }, [
    brandColumn,
    columns,
    typeColumn,
    editColumn,
    currentId?.loid,
    isBrandVisible,
    loanOfficerColumn
  ])

  const periodOptions = [
    {
      key: 30,
      label: "30 Days",
      shortLabel: "30 D",
      onClick: () => setPeriod(30)
    },
    {
      key: 7,
      label: "7 Days",
      shortLabel: "7 D",
      onClick: () => setPeriod(7)
    },
    {
      key: 1,
      label: "1 Day",
      shortLabel: "1 D",
      onClick: () => setPeriod(1)
    }
  ]

  const closeCardDetailsPopOver = () => {
    setSelected(undefined)
  }

  const handleChange = (selectedId: string) => {
    setSelectedOption(selectedId)
  }

  const filteredDataList = useMemo(() => {
    switch (selectedOption) {
      case PRICING_LOGS_OPTION_IDS.SUCCESSFUL_REQUESTS:
        return sortedList.filter(item => isNotZeroString(item.base_rate))
      case PRICING_LOGS_OPTION_IDS.FAILED_REQUESTS:
        return sortedList.filter(item => !isNotZeroString(item.base_rate))
      default:
        return sortedList
    }
  }, [selectedOption, sortedList])

  return (
    <div className="px-6 py-6">
      <Table
        columns={tableColumns}
        data={filteredDataList}
        viewType="table"
        searchPlaceholder="Search logs"
        canChangeType={false}
        shrinkFilterSort
        noSorter
        extraHeaderElement={
          <Fragment>
            <Select
              label=""
              selected={selectedOption}
              data={PRICING_LOGS_OPTIONS}
              IDkey="id"
              selectClassName="w-auto md:w-48 mb-2 md:mb-0"
              setSelected={handleChange}
            />
            {showBrandSelector && (
              <Select
                label=""
                selected={selectedBrand}
                data={brandOptions}
                IDkey="id"
                selectClassName="w-auto md:w-48 ml-0 md:ml-2 mb-2 md:mb-0"
                setSelected={setSelectedBrand}
              />
            )}
            <ButtonGroup
              className="ml-auto self-center"
              active={period}
              buttons={periodOptions}
            />
          </Fragment>
        }
        extraHeaderElementClassName="w-full flex flex-col md:flex-row ml-auto md:ml-1"
        searchClassName="pl-10 py-3 lg:min-w-[20rem] text-sm"
        extraElementBeforeTable={
          <Fragment>
            {showBrandSelector && (
              <MonthlyStats
                orgId={currentId?.org_id}
                brandId={currentId?.bank_id}
                onChartClick={setChartFilter}
                selectedBarIndex={chartFilter?.index ?? null}
              />
            )}
            {/* Filter Chips - shown when chart filter is active */}
            {chartFilter && (
              <div className="bg-white px-6 py-3 border-t border-b border-gray-200 flex items-center gap-3 flex-wrap">
                <span className="text-sm text-gray-600 font-medium">
                  Active Filters:
                </span>

                {/* Date Filter Chip */}
                <div className="inline-flex items-center gap-2 px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm">
                  <span>📅 {chartFilter.category}</span>
                  <button
                    onClick={() => setChartFilter(null)}
                    className="hover:bg-blue-200 rounded-full p-0.5 transition-colors"
                    title="Clear filter"
                  >
                    <X size={14} />
                  </button>
                </div>

                {/* Status Filter Chip */}
                <div
                  className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-sm ${
                    chartFilter.status === "success"
                      ? "bg-green-100 text-green-800"
                      : "bg-red-100 text-red-800"
                  }`}
                >
                  <span>
                    {chartFilter.status === "success"
                      ? "✅ Successful"
                      : "❌ Failed"}
                  </span>
                  <button
                    onClick={() => setChartFilter(null)}
                    className={`rounded-full p-0.5 transition-colors ${
                      chartFilter.status === "success"
                        ? "hover:bg-green-200"
                        : "hover:bg-red-200"
                    }`}
                    title="Clear filter"
                  >
                    <X size={14} />
                  </button>
                </div>

                {/* Result Count Badge */}
                <div className="ml-auto text-sm text-gray-600">
                  Showing{" "}
                  <span className="font-semibold">{sortedList.length}</span> of{" "}
                  <span className="font-semibold">
                    {pricingLogs?.length || 0}
                  </span>{" "}
                  logs
                </div>
              </div>
            )}
          </Fragment>
        }
        pageSize={25}
        isLoading={isPricingLogsLoading}
      />
      <PricingLogCardDetails
        open={!!selected}
        onClose={closeCardDetailsPopOver}
        data={selected}
      />
    </div>
  )
}

export default PricingLogs
